import { useRef, useState } from "react";
import { useAuth } from "@/auth/useAuth";
import { featureFlagMutationToken } from "@/auth/featureFlagAuthentication";
import { createFeatureFlag, updateFeatureFlag, type FeatureFlagCommand } from "@/api/featureFlags";
import { AdminApiError } from "@/api/client";
import { env } from "@/config/env";
import type { FeatureFlagRecord } from "@/firestore/featureFlags";

export const STALE_FLAG_MESSAGE =
  "Someone changed this flag while you were editing — reload, review the latest values, and try again.";

/** Maps the backend outcome the operator can act on; everything else is surfaced verbatim. */
export function featureFlagCommandError(error: unknown): Error {
  if (error instanceof AdminApiError && error.conflict) return new Error(STALE_FLAG_MESSAGE);
  return error instanceof Error ? error : new Error(String(error));
}

/** One write of a vendor-wide batch. */
export interface FeatureFlagBatchItem {
  key: string;
  command: FeatureFlagCommand;
}

export type FeatureFlagBatchResult =
  | { key: string; ok: true; record: FeatureFlagRecord }
  | { key: string; ok: false; error: string };

function send(token: string, command: FeatureFlagCommand): Promise<FeatureFlagRecord> {
  return command.kind === "create"
    ? createFeatureFlag(token, command.input)
    : updateFeatureFlag(token, command.id, command.updatedAt, command.input);
}

/**
 * Runs create/update commands against `/featureFlags` with a freshly verified operator token
 * (`useAppVersionPolicyCommand` pattern). No idempotency key: creates are unique per key and
 * updates are guarded by `updatedAt`, so a retried command is either a 400/409 or a no-op.
 */
export function useFeatureFlagCommand() {
  const { user, claims, capabilities } = useAuth();
  const uid = user?.uid ?? "";
  const operatorId = claims?.backendId ?? "";
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const canAct = !!uid && !!operatorId && capabilities.act && (!env.isProd || capabilities.actProd);

  async function run<T>(work: (token: string) => Promise<T>): Promise<T> {
    if (!canAct) throw new Error("Your role cannot change feature flags.");
    if (inFlight.current) throw new Error("An action is already in progress.");
    inFlight.current = true;
    setPending(true);
    try {
      return await work(await featureFlagMutationToken(uid, operatorId));
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }

  async function execute(command: FeatureFlagCommand): Promise<FeatureFlagRecord> {
    try {
      return await run((token) => send(token, command));
    } catch (error) {
      throw featureFlagCommandError(error);
    }
  }

  /**
   * A vendor-wide pause / resume: one verified token, then the writes one at a time in the given
   * order. A failed write is reported and the batch carries on — during a maintenance window
   * every other flag that can be switched should be.
   */
  async function executeAll(
    items: readonly FeatureFlagBatchItem[],
  ): Promise<FeatureFlagBatchResult[]> {
    try {
      return await run(async (token) => {
        const results: FeatureFlagBatchResult[] = [];
        for (const item of items) {
          try {
            results.push({ key: item.key, ok: true, record: await send(token, item.command) });
          } catch (error) {
            results.push({
              key: item.key,
              ok: false,
              error: featureFlagCommandError(error).message,
            });
          }
        }
        return results;
      });
    } catch (error) {
      throw featureFlagCommandError(error);
    }
  }

  return { execute, executeAll, pending, canAct, operatorId };
}
